import NiceModal from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useEffect, useMemo, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import AutoComplete from 'components/field/AutoComplete';
import Button from 'components/field/Button';
import DateInput from 'components/field/DateInput';
import IntegerInput from 'components/field/IntegerInput';
import Status from 'components/field/Status';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import ConfirmationModal from 'components/global/ConfirmationModal';
import ImportByTemplate from 'components/global/ImportByTemplate';
import MinimizeButton from 'components/global/MinimizeButton';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import { updateDynamicListData } from 'components/list/dynamicListSlice';
import Table from 'components/table/Table';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useModalRouter from 'hooks/useModalRouter';
import { usePrompt } from 'hooks/usePrompt';

import { ADVANCE_WMS_MODULE, AUTOCOMPLETE, FORM_IMPORT, WMS_MODULE } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { DYNAMICLISTS } from 'utils/constants';

import { faDownload, faPlusCircle } from '@fortawesome/pro-solid-svg-icons';

import PartialPutModal from './PartialPutModal';

const disabledFields = {
	add: { startDate: true, productName: true, qtyPut: true, binLocationID: true },
	edit: {
		warehouseID: true,
		fromLocationID: true,
		productName: true,
		qtyPut: true,
		binLocationID: true,
		startDate: true
	},
	complete: {
		warehouseID: true,
		fromLocationID: true,
		startDate: true,
		productID: true,
		productName: true,
		quantity: true,
		suggestedBins: true,
		sku: true
	},
	view: {
		warehouseID: true,
		responsibleID: true,
		fromLocationID: true,
		startDate: true,
		productID: true,
		productName: true,
		quantity: true,
		suggestedBins: true,
		qtyPut: true,
		binLocationID: true
	}
};

const PutawayForm = NiceModal.create(({ modalName, mode: modeProp, id }) => {
	const mode = modeProp || 'add';
	const [isMinimizing, setIsMinimizing] = useState(false);

	const schemaDisabledMode = mode === 'createPutaway' ? 'add' : mode;

	const schema = useMemo(
		() =>
			yup.object().shape({
				warehouseID: disabledFields[schemaDisabledMode]?.warehouseID
					? yup.number().nullable()
					: yup.number().required(),
				responsibleID: disabledFields[schemaDisabledMode]?.responsibleID
					? yup.number().nullable()
					: yup.number().required(),
				startDate: disabledFields[schemaDisabledMode]?.startDate
					? yup.string().nullable()
					: yup.string().required(),
				fromLocationID: disabledFields[schemaDisabledMode]?.fromLocationID
					? yup.number().nullable()
					: yup.number().required(),
				orderItems: yup.array().of(
					yup.object().shape({
						productID: disabledFields[schemaDisabledMode]?.productID
							? yup.number().nullable()
							: yup.number().required(),
						quantity: disabledFields[schemaDisabledMode]?.quantity
							? yup.number().nullable()
							: yup.number().required(),
						suggestedBins: yup.array().nullable(),
						sku: disabledFields[schemaDisabledMode]?.sku
							? yup.string().nullable()
							: yup.string().required(),
						qtyPut: yup.string().nullable(),
						binLocationID: disabledFields[schemaDisabledMode]?.binLocationID
							? yup.number().nullable()
							: yup
									.number()
									.nullable()
									.when('qtyPut', {
										is: (val) => val !== null && val !== undefined && val !== '',
										then: (schema) => schema.required()
									})
					})
				)
			}),
		[mode]
	);

	const axios = useAxiosPrivate();
	const dispatch = useDispatch();

	const { showError, showSuccess } = useAlert();
	const { closeModal } = useModalRouter();
	const handleCloseModal = () => closeModal();

	const fillForm = async () => {
		const defaultValues = {
			status: 'new',
			orderItems: [{}]
		};

		if (!id) {
			return defaultValues;
		}
		const URL = mode === 'createPutaway' ? WMS_MODULE.GET_INBOUND : ADVANCE_WMS_MODULE.GET_PUTAWAY_BY_ID;

		try {
			const { data } = await axios.post(URL, { ID: parseInt(id) });
			if (data?.orderItems) {
				data.orderItems.sort((a, b) => a.sku.localeCompare(b.sku));
				data.orderItems.forEach((item) => {
					if (item.suggestedBins) {
						item.suggestedBins = item.suggestedBins.split(',');
					}
				});
			}

			return {
				...defaultValues,
				...data
			};
		} catch (error) {
			AppLogger.error(error);
			showError();
		}
	};

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: fillForm
	});

	const {
		watch,
		setValue,
		getValues,
		control,
		handleSubmit,
		formState: { isDirty, isSubmitSuccessful, isSubmitting, isLoading: isFormLoading }
	} = formMethods;

	usePrompt(isDirty && !isSubmitSuccessful && !isSubmitting && !isMinimizing);
	const tableData = watch('tableData');

	const getTitle = () => {
		if (mode === 'complete') return 'completePutAway';
		if (mode === 'view') return 'viewPutAway';
		if (mode === 'edit') return 'editPutAway';
		return 'addPutAway';
	};

	const getSubmitButtonLabel = () => {
		if (mode === 'complete' || mode === 'edit' || mode === 'createPutaway') return 'save';
		return 'button.add';
	};

	const responsibleAutocompleteColumns = useMemo(() => [{ accessor: 'Name', width: 200 }], []);
	const binAutocompleteColumns = useMemo(() => [{ accessor: 'Name', width: 200 }], []);

	const colSpan = { md: 6 };

	const productAutocompleteColumns = useMemo(
		() => [
			{ accessor: 'SKU', width: 200 },
			{ accessor: 'name', width: 200 }
		],
		[]
	);

	const productImportTemplate = {
		icon: faDownload,
		title: 'importByTemplate',
		role: 'importByTemplate',
		downloadUrl: FORM_IMPORT.GET_FORM_IMPORT_PRODUCT_TEMPLATE,
		uploadUrl: FORM_IMPORT.IMPORT_PRODUCT_TEMPLATE,
		acceptedFileTypes: ['.xlsx', '.xls']
	};

	useEffect(() => {
		if (tableData) {
			// if you has default row
			const { productID, quantity } = watch('orderItems[0]');

			if (!productID && !quantity && mode !== 'edit' && mode !== 'view') {
				deleteRow({ index: 0 });
			}

			const newProducts = tableData.map((item) => ({
				sku: item.SKU,
				productID: item.productID,
				quantity: item.quantity
			}));

			append(newProducts);

			const existingProducts = getValues('orderItems');

			newProducts.forEach((item, index) => {
				if (index >= existingProducts.length) {
					['sku', 'productID', 'quantity'].forEach((field) => {
						setValue(`orderItems[${index}].${field}`, item[field], {
							shouldDirty: true,
							shouldValidate: true
						});
					});
				}
			});
		}
	}, [tableData]);

	const tableColumns = useMemo(() => {
		const columns = [
			{
				id: 'sku',
				header: 'productName',
				size: 150,
				renderCell: ({ row }) => (
					<AutoComplete
						autoCompleteName={AUTOCOMPLETE.PRODUCTS}
						name={`orderItems[${row.index}].productID`}
						value={watch(`orderItems[${row.index}].productID`)}
						setValue={(value) => {
							setValue(`orderItems[${row.index}].productID`, value?.ID ?? null, {
								shouldDirty: true,
								shouldValidate: true
							});
							setValue(`orderItems[${row.index}].sku`, value?.SKU ?? null, {
								shouldDirty: true,
								shouldValidate: true
							});
						}}
						columns={productAutocompleteColumns}
						selectionLabelAccessor={['SKU', 'name']}
						viewMode={disabledFields[schemaDisabledMode]?.productID}
					/>
				)
			},
			{
				id: 'suggestedBins',
				header: 'suggestedBins',
				size: 150,
				renderCell: ({ row }) => (
					<AutoComplete
						autoCompleteName={AUTOCOMPLETE.PUT_AND_PICK_BINS_BY_WAREHOUSE}
						columns={responsibleAutocompleteColumns}
						name={`orderItems[${row.index}].suggestedBins`}
						value={
							Array.isArray(watch(`orderItems[${row.index}].suggestedBins`))
								? watch(`orderItems[${row.index}].suggestedBins`)
								: watch(`orderItems[${row.index}].suggestedBins`)?.split(',')
						}
						setValue={(value) =>
							setValue(`orderItems[${row.index}].suggestedBins`, value?.map((item) => item.ID) ?? null, {
								shouldDirty: true,
								shouldValidate: true
							})
						}
						selectionLabelAccessor={['Name']}
						disabled={disabledFields[schemaDisabledMode]?.suggestedBins}
						multipleSelection
						filterObject={{
							warehouseID: watch('warehouseID')
						}}
					/>
				)
			},
			{
				id: 'qtyPut',
				header: 'qtyPut',
				renderCell: ({ row }) => (
					<IntegerInput
						viewMode={disabledFields[schemaDisabledMode]?.qtyPut || row.original.locked}
						maxPlaceholder={mode === 'view' || mode === 'complete' ? row.original.quantity : undefined}
						max={mode === 'view' || mode === 'complete' ? row.original.quantity : undefined}
						value={watch(`orderItems[${row.index}].qtyPut`)}
						setValue={(value) => {
							if ((mode === 'view' || mode === 'complete') && row.original.quantity < value) return;
							setValue(`orderItems[${row.index}].qtyPut`, value, {
								shouldDirty: true,
								shouldValidate: true
							});
						}}
						showArrows={false}
					/>
				),
				size: 75
			},
			{
				id: 'binLocation',
				header: 'binLocation',
				renderCell: ({ row }) => (
					<AutoComplete
						autoCompleteName={AUTOCOMPLETE.PUT_AND_PICK_BINS_BY_BIN_ID}
						columns={responsibleAutocompleteColumns}
						name={`orderItems[${row.index}].binLocationID`}
						value={watch(`orderItems[${row.index}].binLocationID`)}
						setValue={(value) =>
							setValue(`orderItems[${row.index}].binLocationID`, value?.ID ?? null, {
								shouldDirty: true,
								shouldValidate: true
							})
						}
						selectionLabelAccessor={['Name']}
						viewMode={disabledFields[schemaDisabledMode]?.binLocationID || row.original.locked}
						filterObject={{
							binID: watch('fromLocationID')
						}}
					/>
				),
				size: 75
			}
		];

		if (mode === 'complete') {
			columns.push({
				id: 'action',
				header: 'action',
				renderCell: ({ row }) => {
					if (row.original.locked)
						return (
							<Button
								label="removeOnPutaway"
								onClick={() => {
									deleteRow(row);
								}}
								disabled={!row.original.locked}
							/>
						);
					return (
						<Button
							label="partialPut"
							onClick={() => {
								NiceModal.show(PartialPutModal, {
									insert,
									setValue,
									id: watch(`orderItems[${row.index}].id`),
									productID: row.original.productID,
									quantity: row.original.quantity,
									sku: row.original.sku,
									fromLocationID: watch('fromLocationID'),
									index: row.index
								});
							}}
							disabled={
								!!watch(`orderItems[${row.index}].qtyPut`) ||
								row.original.locked ||
								watch(`orderItems[${row.index}].quantity`) === 0
							}
						/>
					);
				},
				size: 75
			});
		}

		if (mode !== 'view' && mode !== 'complete') {
			columns.splice(1, 0, {
				id: 'quantity',
				header: 'quantity',
				renderCell: ({ row }) => (
					<IntegerInput
						name={`orderItems[${row.index}].quantity`}
						viewMode={disabledFields[schemaDisabledMode]?.quantity}
						showArrows={false}
					/>
				),
				size: 75
			});
			columns.push({
				id: 'deleteRow',
				size: 20
			});
		}
		return columns;
	}, [watch]);

	const deleteRow = (row) => {
		if (mode === 'add' || mode === 'edit') {
			remove(row.index);
			return;
		}
		const orderItems = getValues('orderItems');
		const rowId = orderItems[row.index].id;
		const mainRowIndex = orderItems.findIndex((item) => item.id === rowId);
		setValue(
			`orderItems[${mainRowIndex}].quantity`,
			orderItems[mainRowIndex].quantity + orderItems[row.index].qtyPut,
			{
				shouldDirty: true,
				shouldValidate: true
			}
		);
		remove(row.index);
	};

	const onSubmit = async (data, e, confirmed) => {
		if (!confirmed && mode === 'complete') {
			NiceModal.show(ConfirmationModal, {
				title: 'completePutAway',
				subTitle: 'completePuttingAlert',
				yesAction: () => {
					try {
						onSubmit(data, {}, true);
					} catch (error) {
						AppLogger.error(error);
					}
				},
				noAction: () => closeModal()
			});
			return;
		}
		const newData = { ...data };
		let orderItems = [...newData.orderItems];
		const { ...rest } = newData;
		delete rest.orderItems;

		// remove disabled fields from data
		Object.keys(rest).forEach((key) => {
			if (disabledFields[mode]?.[key]) {
				if (mode === 'edit' && key === 'startDate') return;
				delete rest[key];
			}
		});
		orderItems.forEach((item) => {
			if (item.qtyPut) item.qtyPut = parseInt(item.qtyPut);
			if (item.suggestedBins) item.suggestedBins = item.suggestedBins.join(',');
			Object.keys(item).forEach((key) => {
				if (disabledFields[mode]?.[key]) {
					delete item[key];
				}
			});
		});

		if (mode === 'complete') {
			orderItems = orderItems.filter((item) => parseInt(item.qtyPut) > 0);
		}

		const URL =
			mode === 'complete'
				? ADVANCE_WMS_MODULE.COMPLETE_PUTAWAY
				: mode === 'edit'
					? ADVANCE_WMS_MODULE.UPDATE_PUTAWAY
					: ADVANCE_WMS_MODULE.ADD_PUTAWAY;

		try {
			const response = await axios.post(URL, { ...rest, orderItems, id: parseInt(id) });
			if (response.status === 200 && response.data.success !== false) {
				dispatch(updateDynamicListData(DYNAMICLISTS.PUTAWAY_LIST));
				showSuccess();
				closeModal();
				return;
			}
			if (response.data.code && response.data.code === 'NA') {
				showError({
					header: 'errorPage.header',
					message: 'noInventoryForProcess'
				});
			} else if (response.data.code && response.data.code === 'NEQ') {
				showError({
					header: 'errorPage.header',
					message: 'notEnoughForProduct'
				});
			} else {
				showError();
			}
			AppLogger.error(response);
		} catch (error) {
			AppLogger.error(error);
			showError();
		}
	};

	const loading = isFormLoading || isSubmitting;
	const { fields, append, insert, remove } = useFieldArray({
		name: 'orderItems',
		control
	});

	const warehouseAutocompleteColumns = [
		{ accessor: 'Name', width: 200 },
		{ accessor: 'Code', width: 150 }
	];
	const warehouseAutocompleteLabels = useMemo(() => ['Name'], []);
	const warehouseAutoComplete = watch('warehouseID');

	const isWarehouseSelected = !!watch('warehouseID');
	return (
		<Modal
			name={modalName}
			fullwidth
			maxWidth="large"
			minimizeButton={(show, close) => (
				<MinimizeButton
					onShow={() => {
						show();
						setIsMinimizing(false);
					}}
					onClose={close}
					label={getTitle()}
				/>
			)}
			onMinimize={() => {
				setIsMinimizing(true);
			}}>
			<ModalTitle>
				<Typography variant="p1">{getTitle()}</Typography>
			</ModalTitle>
			<ModalContent>
				<Form formMethods={formMethods}>
					<FormItem
						colSpan={colSpan}
						label="warehouse"
						roleName="MultiWarehouseModule"
						required={!disabledFields[schemaDisabledMode]?.warehouseID}>
						<AutoComplete
							autoCompleteName={AUTOCOMPLETE.WAREHOUSES}
							name="warehouseID"
							columns={warehouseAutocompleteColumns}
							selectionLabelAccessor={warehouseAutocompleteLabels}
							value={warehouseAutoComplete}
							setValue={(value, firstRender) => {
								if (firstRender) return;
								const warehouseValue = value?.ID ?? null;
								if (warehouseValue === warehouseAutoComplete) return;
								setValue('warehouseID', warehouseValue, {
									shouldValidate: true,
									shouldDirty: true
								});
								setValue('responsibleID', null, { shouldValidate: true, shouldDirty: true });
								setValue('fromLocationID', null, { shouldValidate: true, shouldDirty: true });
							}}
							disabled={disabledFields[schemaDisabledMode]?.warehouseID}
						/>
					</FormItem>
					<FormItem
						label="responsible"
						colSpan={colSpan}
						isLoading={loading}
						required={!disabledFields[schemaDisabledMode]?.responsibleID}>
						<AutoComplete
							autoCompleteName={
								(schemaDisabledMode === 'add' || schemaDisabledMode === 'edit') && isWarehouseSelected
									? AUTOCOMPLETE.OTO_PACK_USERS_BY_WAREHOUSE
									: AUTOCOMPLETE.OTO_PACK_USERS
							}
							columns={responsibleAutocompleteColumns}
							name={`responsibleID`}
							value={watch(`responsibleID`)}
							setValue={(value, firstRender) => {
								if (firstRender) return;
								setValue(`responsibleID`, value?.ID ?? null, {
									shouldValidate: true,
									shouldDirty: true
								});
							}}
							selectionLabelAccessor={['Name']}
							disabled={disabledFields[mode]?.responsibleID || !isWarehouseSelected}
							filterObject={{
								warehouseID: warehouseAutoComplete
							}}
						/>
					</FormItem>
					<FormItem
						label="fromBin"
						colSpan={colSpan}
						isLoading={loading}
						required={!disabledFields[schemaDisabledMode]?.fromLocationID}>
						<AutoComplete
							autoCompleteName={
								schemaDisabledMode === 'add' ? AUTOCOMPLETE.BINS_BY_WAREHOUSE : AUTOCOMPLETE.BINS
							}
							columns={binAutocompleteColumns}
							name={`fromLocationID`}
							value={watch(`fromLocationID`)}
							setValue={(value, firstRender) => {
								if (firstRender) return;
								setValue(`fromLocationID`, value?.ID ?? null, {
									shouldValidate: true,
									shouldDirty: true
								});
							}}
							selectionLabelAccessor={['Name']}
							disabled={disabledFields[mode]?.fromLocationID || !isWarehouseSelected}
							filterObject={{
								warehouseID: warehouseAutoComplete
							}}
						/>
					</FormItem>
					<FormItem label="startDate" colSpan={colSpan} isLoading={loading} disabled={true}>
						<DateInput
							name="startDate"
							value={watch('startDate')}
							setValue={(date) =>
								setValue('startDate', date ?? null, { shouldDirty: true, shouldValidate: true })
							}
							disabled={true}
						/>
					</FormItem>
					<FormItem label="status" colSpan={colSpan} isLoading={loading}>
						<Status value={watch('status')} />
					</FormItem>

					<Table
						columns={tableColumns}
						data={fields}
						loading={loading}
						onRowDelete={deleteRow}
						onRowAdd={() => {
							append({});
						}}
						initEditMode={() => true}
						footerComponent={
							mode !== 'view' ? (
								<div className="mt-4" style={{ display: 'flex' }}>
									<Button
										className="mx-3"
										variant="blackOutline"
										label="addNewRow"
										icon={faPlusCircle}
										onClick={() => {
											append({});
										}}
									/>
									<ImportByTemplate setValue={setValue} importActionDetails={productImportTemplate} />
								</div>
							) : null
						}
					/>
				</Form>
			</ModalContent>
			<ModalActions>
				<Button label="cancel" variant="outline" onClick={handleCloseModal} />
				{mode !== 'view' && (
					<Button
						label={getSubmitButtonLabel()}
						disabled={!isDirty}
						disabledTooltip={!isDirty && 'formInfo.noChanges'}
						loading={loading}
						onClick={handleSubmit(onSubmit, (errors) => {
							AppLogger.log(errors);
						})}
					/>
				)}
			</ModalActions>
		</Modal>
	);
});

export default PutawayForm;
